import { ConfigProvider, Dropdown, Space, Table, Tooltip } from "antd";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Assets } from "../../../constants/images";
import {
  ArrowDown,
  BinocularsLight,
  EditIcon,
  MoreIcon,
  NoDataIcon,
  PurpleRepeatIcon,
  RecycleBin,
  ShareIcon,
} from "../../../constants/svg";

import moment from "moment";
import { FormProvider, useForm } from "react-hook-form";
import { useDispatch, useSelector } from "react-redux";
import { NavLink } from "react-router-dom";
import { useStateContext } from "../../../Contexts/ContextProvider";
import { GetAuthUserLocalStorage } from "../../../services/localStorage/localStorage";
import {
  deleteTask,
  deleteTaskList,
  getTaskLists,
  getTasks,
} from "../../../services/tasks/frontend-tasks";
import {
  setIsTaskListsLoading,
  setIsTasksLoading,
  setTaskLists,
  setTasks,
} from "../../../store/slices/tasksSlice";
import DeleteModal from "../../Modals/DeleteModal";
import GenericModal from "../../Modals/GenericModal";
import ShareModal from "../../Modals/ShareModal";
import TaskModalForms from "./Modals/TaskModalForms";
import TaskListModalForm from "./Modals/TaskListModalForm";
import { tableLoader } from "../../../utils/helper";
import {
  roles as salonRoles,
  SHARE_URL,
  tableHeight,
  task_groups,
  task_type,
} from "../../../utils/constants";
import { getShowUpcomingTasks } from "../../../utils/commons";

const TASK_KNOWN_TYPE_SET = new Set(task_groups.map((g) => g.type));
const TABLE_COL_COUNT = 6;

function TaskBody(props) {
  const { tasks, isTasksLoading, taskLists } = useSelector(
    (state) => state?.tasks,
  );
  const { currentSalonId } = useSelector((state) => state.global);
  const dispatch = useDispatch();
  const methods = useForm();

  const [showModal, setShowModal] = useState({
    shareModal: null,
    editModal: null,
    deleteModal: null,
    listDeleteModal: null,
  });
  const [open, setOpen] = useState(false);
  const [selectedData, setSelectedData] = useState(null);
  const [selectedList, setSelectedList] = useState(null);
  const [showListEditModal, setShowListEditModal] = useState(false);
  const authUser = GetAuthUserLocalStorage();
  const { id: userId } = authUser;
  const isAdmin = [
    salonRoles.admin,
    salonRoles.owner,
    salonRoles.super_admin,
  ].includes(authUser?.roles?.[0]?.id);

  const url = window.location.href + "/" + selectedData?.key;
  const [disable, setDisable] = useState(false);
  const [isDragging, setIsDragging] = useState(false);

  const [showGenericModal, setShowGenericModal] = useState(false);
  const { taskPopupTitle, setTaskPopupTitle } = useStateContext();

  const [tableSort, setTableSort] = useState({
    columnKey: null,
    order: null,
  });
  const [expandedByType, setExpandedByType] = useState({});

  function renderAttendeesColumn(attendees) {
    if (!attendees) {
      return null;
    }
    const attendeeNames = attendees.attendents || [];
    const attendeeTooltipContent = (
      <div className="attendeeNames">
        {attendeeNames.slice(0, 7).map((name, index) => (
          <React.Fragment key={index}>
            <span>{name}</span>
            {index < attendeeNames.slice(0, 7).length - 1}
          </React.Fragment>
        ))}
        {attendeeNames.length > 7 && (
          <>
            <strong>+{attendeeNames.length - 7} more</strong>
          </>
        )}
      </div>
    );

    return (
      <div className="">
        {attendees.count != 0 ? (
          <Tooltip
            title={attendeeTooltipContent}
            placement="bottom"
            overlayClassName="custom-tooltip"
            color="purple"
            // open={true}
          >
            <div className="seenBy dottedRoundedBox">
              <span>{attendees.count}</span>
            </div>
          </Tooltip>
        ) : (
          <div className="seenBy dottedRoundedBox is-zero">
            <span>{attendees.count}</span>
          </div>
        )}
      </div>
    );
  }

  const actionMenu = (record) => {
    const viewItem = {
      key: "1",
      label: (
        <>
          <NavLink to={`/tasks/details/${record?.key}`} className={"black"}>
            <BinocularsLight />
            View
          </NavLink>
        </>
      ),
    };
    const shareItem = {
      key: "3",
      label: (
        <>
          <div
            onClick={() => {
              handleSetSelectedData(record);
              setShowModal((prevState) => ({
                ...prevState,
                shareModal: true,
              }));
            }}
          >
            <ShareIcon />
            Share
          </div>
        </>
      ),
    };

    // Tasks that belong to a task list are managed from the list itself, so a
    // single occurrence only gets View + Share (no Edit / Delete).
    if (record?.taskListId) {
      return [viewItem, shareItem];
    }

    return [
      viewItem,
      {
        key: "2",
        label: (
          <>
            <div
              onClick={() => {
                handleSetSelectedData(record);
                setOpen(true);
                setShowGenericModal(true);
                setTaskPopupTitle("Edit Task");
              }}
            >
              <EditIcon />
              Edit
            </div>
          </>
        ),
      },
      shareItem,
      {
        key: "4",
        label: (
          <div
            className={"deleteLabel"}
            onClick={() => {
              handleSetSelectedData(record);
              setShowModal((prevState) => ({
                ...prevState,
                deleteModal: true,
              }));
            }}
          >
            <RecycleBin />
            Delete
          </div>
        ),
      },
    ];
  };

  const listActionMenu = (list) => {
    return [
      {
        key: "edit",
        label: (
          <div
            onClick={() => {
              setSelectedList(list);
              setShowListEditModal(true);
            }}
          >
            <EditIcon />
            Edit
          </div>
        ),
      },
      {
        key: "delete",
        label: (
          <div
            className={"deleteLabel"}
            onClick={() => {
              setSelectedList(list);
              setShowModal((prevState) => ({
                ...prevState,
                listDeleteModal: true,
              }));
            }}
          >
            <RecycleBin />
            Delete
          </div>
        ),
      },
    ];
  };

  const handleSetSelectedData = (record) => {
    setSelectedData({
      key: record?.key,
      type: record?.type,
      name: record?.taskName,
      description: record?.description,
      image: record?.taskName?.image,
      start_date: moment(record?.start_date).format("MMM DD, yyyy"),
      start_time: moment(record?.start_date).format("hh:mm a"),
    });
  };

  const fetchTasks = async () => {
    const params = {
      is_paginated: 0,
      salon_id: currentSalonId,
      type: props?.selectedCategory,
      // page: props?.page?.currentPage,
      platform: "web",
      // "per-page": props?.page?.perPage,
      all_time: true,
    };

    await getTasks(params, dispatch, setIsTasksLoading, setTasks);
  };

  const fetchTaskLists = async () => {
    await getTaskLists(
      { salon_id: currentSalonId },
      dispatch,
      setIsTaskListsLoading,
      setTaskLists,
    );
  };

  const handleDelete = async () => {
    await deleteTask(selectedData, setDisable, showModal, setShowModal);
    fetchTasks();
  };

  const handleDeleteList = async () => {
    await deleteTaskList(
      selectedList?.id,
      { salon_id: currentSalonId },
      setDisable,
      () => {
        setShowModal((prevState) => ({ ...prevState, listDeleteModal: false }));
        fetchTaskLists();
        fetchTasks();
      },
    );
  };

  useEffect(() => {
    const handleMouseUp = () => {
      setIsDragging(false);
    };

    window.addEventListener("mouseup", handleMouseUp);

    return () => {
      window.removeEventListener("mouseup", handleMouseUp);
    };
  }, []);

  useEffect(() => {
    fetchTasks();
  }, [
    props?.selectedCategory,
    // props?.page?.currentPage,
    // props?.page?.perPage,
    currentSalonId,
  ]);

  useEffect(() => {
    fetchTaskLists();
  }, [currentSalonId]);

  const filteredData = useMemo(() => {
    const currentDate = moment().format("YYYY-MM-DD");
    if (props.activeTab === "tab1") {
      return getShowUpcomingTasks(tasks.data, userId);
    } else {
      return tasks.data.filter((task) =>
        moment(task.dueDate).isBefore(currentDate),
      );
    }
  }, [tasks, props.activeTab, userId]);

  const sortedTaskRows = useMemo(() => {
    const copy = [...filteredData];
    if (!tableSort.columnKey || !tableSort.order) {
      return copy;
    }
    if (tableSort.columnKey === "taskName") {
      copy.sort((a, b) => {
        const cmp = (a.taskName || "").localeCompare(b.taskName || "");
        return tableSort.order === "ascend" ? cmp : -cmp;
      });
    } else if (tableSort.columnKey === "dueDate") {
      copy.sort((a, b) => {
        const dateA = moment(a.dueDate, "MMM DD, YYYY", true);
        const dateB = moment(b.dueDate, "MMM DD, YYYY", true);
        const validA = dateA.isValid();
        const validB = dateB.isValid();
        let cmp = 0;
        if (!validA && !validB) {
          cmp = 0;
        } else if (!validA) {
          cmp = 1;
        } else if (!validB) {
          cmp = -1;
        } else {
          cmp = dateA.valueOf() - dateB.valueOf();
        }
        return tableSort.order === "ascend" ? cmp : -cmp;
      });
    }
    return copy;
  }, [filteredData, tableSort]);

  useEffect(() => {
    setExpandedByType(() => {
      const next = {};
      task_groups.forEach((g) => {
        next[g.type] = false;
      });
      next.other = false;
      (taskLists?.data || []).forEach((list) => {
        next[`list-${list.id}`] = false;
      });
      return next;
    });
  }, [filteredData, taskLists]);

  const toggleGroup = useCallback((taskType) => {
    setExpandedByType((prev) => ({
      ...prev,
      [taskType]: !prev[taskType],
    }));
  }, []);

  const handleTableChange = (_pagination, _filters, sorter) => {
    const s = Array.isArray(sorter) ? sorter[0] : sorter;
    if (!s || !s.columnKey) {
      setTableSort({ columnKey: null, order: null });
      return;
    }
    setTableSort({
      columnKey: s.columnKey,
      order: s.order ?? null,
    });
  };

  const tableData = useMemo(() => {
    const buckets = {};
    task_groups.forEach((g) => {
      buckets[g.type] = [];
    });
    const other = [];

    sortedTaskRows.forEach((task) => {
      // Task-list occurrences render under their own list section, not the
      // frequency buckets.
      if (task.taskListId) {
        return;
      }
      const t = Number(task.type);
      if (TASK_KNOWN_TYPE_SET.has(t)) {
        buckets[t].push(task);
      } else {
        other.push(task);
      }
    });

    const rows = [];
    task_groups.forEach(({ type, displayLabel }) => {
      const list = buckets[type];
      rows.push({
        isGroupRow: true,
        key: `group-${type}`,
        taskType: type,
        groupLabel: displayLabel,
        groupCount: list.length,
      });
      if (expandedByType[type]) {
        if (list.length === 0) {
          rows.push({
            isGroupEmptyPlaceholder: true,
            key: `empty-${type}`,
            taskType: type,
          });
        } else {
          rows.push(...list);
        }
      }
    });

    if (other.length > 0) {
      rows.push({
        isGroupRow: true,
        key: "group-other",
        taskType: "other",
        groupLabel: "Other Tasks",
        groupCount: other.length,
      });
      if (expandedByType.other) {
        rows.push(...other);
      }
    }

    // Task list sections (after the frequency groups). Each configured list
    // shows as its own collapsible section, even when it has no occurrence in
    // the current tab.
    const listTasksById = {};
    sortedTaskRows.forEach((task) => {
      if (!task.taskListId) return;
      const id = Number(task.taskListId);
      if (!listTasksById[id]) listTasksById[id] = [];
      listTasksById[id].push(task);
    });

    (taskLists?.data || []).forEach((list) => {
      const listTasks = listTasksById[Number(list.id)] || [];
      const expandKey = `list-${list.id}`;
      rows.push({
        isListGroupRow: true,
        showListActions: isAdmin,
        key: `group-${expandKey}`,
        taskType: expandKey,
        groupLabel: list.name,
        groupCount: listTasks.length,
        listData: list,
      });
      if (expandedByType[expandKey]) {
        if (listTasks.length === 0) {
          rows.push({
            isGroupEmptyPlaceholder: true,
            key: `empty-${expandKey}`,
            taskType: expandKey,
          });
        } else {
          rows.push(...listTasks);
        }
      }
    });

    return rows;
  }, [sortedTaskRows, expandedByType, taskLists, isAdmin]);

  const columns = useMemo(
    () => [
      {
        title: "Task Name",
        dataIndex: "taskName",
        key: "taskName",
        sorter: true,
        sortDirections: ["descend", "ascend", "descend"],
        sortOrder: tableSort.columnKey === "taskName" ? tableSort.order : null,
        onCell: (record) => {
          if (
            record.isGroupRow ||
            record.isListGroupRow ||
            record.isGroupEmptyPlaceholder
          ) {
            return {};
          }
          return { style: { fontWeight: 400 } };
        },
        render: (text, record) => {
          if (record.isGroupRow || record.isListGroupRow) {
            const expanded = !!expandedByType[record.taskType];
            return {
              children: (
                <button
                  type="button"
                  className={`taskTable-groupToggle${expanded ? " is-expanded" : ""}`}
                  onClick={() => toggleGroup(record.taskType)}
                >
                  <span className="taskTable-groupCaret" aria-hidden>
                    <ArrowDown />
                  </span>
                  <span className="taskTable-groupTitle">
                    {record.groupLabel} ({record.groupCount})
                  </span>
                </button>
              ),
              // Leave the "More" cell free for the list's Edit/Delete menu.
              props: {
                colSpan: record.showListActions
                  ? TABLE_COL_COUNT - 1
                  : TABLE_COL_COUNT,
              },
            };
          }
          if (record.isGroupEmptyPlaceholder) {
            return {
              children: (
                <div className="taskTable-groupEmptyInner">
                  <p className="taskTable-groupEmptyMessage">
                    No tasks available in this category.
                  </p>
                </div>
              ),
              props: { colSpan: TABLE_COL_COUNT },
            };
          }
          return (
            <NavLink
              to={`/tasks/details/${record?.key}`}
              className={"black taskAnchor"}
              style={{ fontWeight: 400 }}
            >
              {record.type > 10 && <PurpleRepeatIcon />}
              <span className={record.type > 10 && "custom-ml-05"}>
                {record.taskName}
              </span>
            </NavLink>
          );
        },
        width: "35%",
      },
      {
        title: "Due On",
        dataIndex: "dueDate",
        key: "dueDate",
        sorter: true,
        sortDirections: ["descend", "ascend", "descend"],
        sortOrder: tableSort.columnKey === "dueDate" ? tableSort.order : null,
        render: (text, record) => {
          if (
            record.isGroupRow ||
            record.isListGroupRow ||
            record.isGroupEmptyPlaceholder
          ) {
            return { children: null, props: { colSpan: 0 } };
          }
          return <p>{record.dueDate}</p>;
        },
        align: "left",
        width: "10%",
      },
      {
        title: <span className="taskHead bg-purple">TO DO</span>,
        dataIndex: "toDo",
        key: "toDo",
        render: (text, record) => {
          if (
            record.isGroupRow ||
            record.isListGroupRow ||
            record.isGroupEmptyPlaceholder
          ) {
            return { children: null, props: { colSpan: 0 } };
          }
          return renderAttendeesColumn(record.toDo);
        },
        align: "center",
        width: "15%",
      },
      {
        title: <span className="taskHead bg-red">OVERDUE</span>,
        dataIndex: "overdue",
        key: "overdue",
        render: (text, record) => {
          if (
            record.isGroupRow ||
            record.isListGroupRow ||
            record.isGroupEmptyPlaceholder
          ) {
            return { children: null, props: { colSpan: 0 } };
          }
          return renderAttendeesColumn(record.overdue);
        },
        align: "center",
        width: "15%",
      },
      {
        title: <span className="taskHead bg-green">DONE</span>,
        dataIndex: "done",
        key: "done",
        render: (text, record) => {
          if (
            record.isGroupRow ||
            record.isListGroupRow ||
            record.isGroupEmptyPlaceholder
          ) {
            return { children: null, props: { colSpan: 0 } };
          }
          return renderAttendeesColumn(record.done);
        },
        align: "center",
        width: "15%",
      },
      {
        title: "More",
        key: "action",
        align: "center",
        width: "10%",
        render: (_text, record) => {
          if (record.showListActions) {
            return (
              <Space size="middle">
                <Dropdown
                  menu={{
                    items: listActionMenu(record.listData),
                  }}
                  placement="bottomRight"
                  trigger={["click"]}
                  overlayClassName={"defaultDropdown"}
                >
                  <div className="icon cursor-pointer">
                    <MoreIcon />
                  </div>
                </Dropdown>
              </Space>
            );
          }
          if (
            record.isGroupRow ||
            record.isListGroupRow ||
            record.isGroupEmptyPlaceholder
          ) {
            return { children: null, props: { colSpan: 0 } };
          }
          return (
            <Space size="middle">
              <Dropdown
                menu={{
                  items: actionMenu(record),
                }}
                placement="bottomRight"
                trigger={["click"]}
                overlayClassName={"defaultDropdown"}
              >
                <div className="icon cursor-pointer">
                  <MoreIcon />
                </div>
              </Dropdown>
            </Space>
          );
        },
      },
    ],
    [tableSort, expandedByType, toggleGroup],
  );

  return (
    <>
      <div className="content">
        <ConfigProvider
          renderEmpty={() => (
            <div className="noSearchData taskTable-emptyState">
              <NoDataIcon />
              <h5>Tasks you’ve assigned will appear here.</h5>
              <h6>Click “Assign Task” to get started.</h6>
            </div>
          )}
        >
          <Table
            /* locale={{
                emptyText:(   <Empty
                    image={Assets.EmptyWhiteBg}
                    description={''}
                />),
            }} */
            store={{ store: "tasks", param: "tasks" }}
            columns={columns}
            loading={tableLoader(isTasksLoading)}
            dataSource={tableData}
            onChange={handleTableChange}
            onRow={(record) => ({
              className:
                [
                  record.isGroupRow || record.isListGroupRow
                    ? "taskTable-groupRow"
                    : "",
                  record.isListGroupRow ? "taskTable-listGroupRow" : "",
                  record.isGroupEmptyPlaceholder
                    ? "taskTable-groupEmptyRow"
                    : "",
                ]
                  .filter(Boolean)
                  .join(" ") || undefined,
            })}
            bordered={false}
            pagination={false}
            // pagination={{
            //   onChange: (currPage) =>
            //     props?.setPage({ ...props?.page, currentPage: currPage }),
            //   pageSize: props?.page?.perPage,
            //   current: props?.page?.currentPage,
            //   total: tasks?.meta?.total || 0,
            //   pageSizeOptions: [10, 20, 50, 100, "All"],
            //   showSizeChanger: false,
            // }}
            scroll={{ x: "max-content", y: tableHeight?.height }}
            responsive={true}
            size={"large"}
            showSorterTooltip={false}
            className="taskTable"
          />
        </ConfigProvider>
      </div>

      {/* Share Modal */}
      <ShareModal
        data={selectedData}
        shareLink={SHARE_URL.TASKS + selectedData?.key}
        show={showModal.shareModal}
        onHide={() =>
          setShowModal((prevState) => ({ ...prevState, shareModal: false }))
        }
        setModalShow={() =>
          setShowModal((prevState) => ({ ...prevState, shareModal: true }))
        }
        modalTitle={"Share Task"}
        modalSubtitle={
          <>
            Click “Copy Link” below, then &nbsp;{" "}
            <NavLink
              to="/inbox"
              className={"uLine blue-accent-color"}
              target="_blank"
            >
              {" "}
              Open Chat{" "}
            </NavLink>{" "}
            &nbsp; to easily share this task with members.
          </>
        }
        size={"lg"}
        bodyComponent={{
          img: Assets.eventShareModal,
          title: "Advanced Balayage Master Class",
          para1:
            "Lenny Strand will be at our Scarborough location teaching a FREE Advanced Balayage Master Class on September 12. This is a free event and recommended for all stylists.",
          para2: "Sep 12, 2023 @ 9:00 AM EST",
        }}
      />
      {/* Delete Modal */}
      <DeleteModal
        handleDelete={handleDelete}
        disable={disable}
        show={showModal.deleteModal}
        onHide={() => setShowModal({ ...showModal, deleteModal: false })}
        heading="Delete Task?"
        para={
          <>
            Are you sure you want to delete
            <b>
              {" "}
              {selectedData?.type == task_type.one_time ? (
                "this one-time task"
              ) : (
                <b>this recurring task</b>
              )}
              ?
            </b>
            <br />
            Important: This task cannot be recovered.
          </>
        }
        notificationMessage="Task"
      />
      {/* Edit Drawer */}
      <GenericModal
        show={showGenericModal}
        onHide={() => setShowGenericModal(false)}
        setModalShow={setShowGenericModal}
        modalTitle={taskPopupTitle}
        size={"lg"}
        bodyComponent={
          <FormProvider {...methods}>
            <TaskModalForms
              selectedData={selectedData}
              isedit={true}
              methods={methods}
              setShowGenericModal={setShowGenericModal}
              show={showGenericModal}
            />
          </FormProvider>
        }
      />

      {/* Delete Task List Modal */}
      <DeleteModal
        handleDelete={handleDeleteList}
        disable={disable}
        show={showModal.listDeleteModal}
        onHide={() => setShowModal({ ...showModal, listDeleteModal: false })}
        heading="Delete Task List?"
        para={
          <>
            Are you sure you want to delete <b>{selectedList?.name}</b>?
            <br />
            Important: This will remove its upcoming tasks and cannot be
            recovered.
          </>
        }
        notificationMessage="Task list"
      />

      {/* Edit Task List Modal */}
      <GenericModal
        show={showListEditModal}
        onHide={() => setShowListEditModal(false)}
        setModalShow={setShowListEditModal}
        disableLeaveConfirmation={true}
        modalTitle={"Edit Task List"}
        customModalHeader={"customNewTaskModalHeader"}
        size={"lg"}
        bodyComponent={
          <TaskListModalForm
            isEdit={true}
            selectedList={selectedList}
            show={showListEditModal}
            setShow={setShowListEditModal}
            onSaved={() => {
              fetchTaskLists();
              fetchTasks();
            }}
          />
        }
      />
    </>
  );
}

export default TaskBody;
